import { useQuery, useQueryClient } from "@tanstack/react-query";
import { API_URL } from "const/constants";
import axios from "axios";

export const providerAdmissionKeys = {
  root: (providerId) => ["provider-admissions", providerId],
  page: (providerId, params) => ["provider-admissions", providerId, "page", params],
  client: (providerId, clientId) => ["provider-admissions", providerId, "client", clientId],
  claimSummary: (providerId) => ["provider-admissions", providerId, "claim-summary"],
  invoiceCandidates: (providerId, params) => [
    "provider-admissions",
    providerId,
    "invoice-candidates",
    params,
  ],
  invoiceCandidateSummary: (providerId, invoiceType) => [
    "provider-admissions",
    providerId,
    "invoice-candidate-summary",
    invoiceType ?? null,
  ],
};

const CACHED_UNTIL_INVALIDATED = {
  staleTime: Infinity,
  cacheTime: Infinity,
  refetchOnWindowFocus: false,
};

const unwrap = (res) => res?.data?.data;

export const useProviderAdmissionPage = (providerId, params, options = {}) =>
  useQuery({
    queryKey: providerAdmissionKeys.page(providerId, params),
    queryFn: async () => {
      const res = await axios.get(`${API_URL}/provider-admissions/provider/${providerId}/page`, {
        params: {
          page: params.page + 1,
          size: params.size,
          search: params.search || undefined,
          claimFilter: params.claimFilter,
        },
      });
      return unwrap(res) ?? {};
    },
    enabled: Boolean(providerId),
    keepPreviousData: true,
    ...CACHED_UNTIL_INVALIDATED,
    ...options,
  });

export const useProviderAdmissionByClient = (providerId, clientId, options = {}) =>
  useQuery({
    queryKey: providerAdmissionKeys.client(providerId, clientId),
    queryFn: async () => {
      const res = await axios.get(`${API_URL}/provider-admissions/provider/${providerId}/page`, {
        params: { page: 1, size: 1, clientId },
      });
      return unwrap(res)?.content?.[0] ?? null;
    },
    enabled: Boolean(providerId && clientId),
    ...CACHED_UNTIL_INVALIDATED,
    ...options,
  });

export const useProviderClaimSummary = (providerId, options = {}) =>
  useQuery({
    queryKey: providerAdmissionKeys.claimSummary(providerId),
    queryFn: async () => {
      const res = await axios.get(
        `${API_URL}/provider-admissions/provider/${providerId}/claim-summary`
      );
      return unwrap(res) ?? { pendingTerms: 0, remainingClients: 0 };
    },
    enabled: Boolean(providerId),
    ...CACHED_UNTIL_INVALIDATED,
    ...options,
  });

export const useProviderInvoiceCandidates = (providerId, params, options = {}) =>
  useQuery({
    queryKey: providerAdmissionKeys.invoiceCandidates(providerId, params),
    queryFn: async () => {
      const res = await axios.get(
        `${API_URL}/provider-admissions/provider/${providerId}/invoice-candidates`,
        {
          params: {
            page: params.page + 1,
            size: params.size,
            search: params.search || undefined,
            invoiceType: params.invoiceType || undefined,
          },
        }
      );
      return unwrap(res) ?? {};
    },
    enabled: Boolean(providerId) && options.enabled !== false,
    keepPreviousData: true,
    ...CACHED_UNTIL_INVALIDATED,
    ...options,
  });

export const useProviderInvoiceCandidateSummary = (providerId, invoiceType, options = {}) =>
  useQuery({
    queryKey: providerAdmissionKeys.invoiceCandidateSummary(providerId, invoiceType),
    queryFn: async () => {
      const res = await axios.get(
        `${API_URL}/provider-admissions/provider/${providerId}/invoice-candidates/summary`,
        { params: { invoiceType: invoiceType || undefined } }
      );
      return unwrap(res) ?? { clientCount: 0, termCount: 0, zeroComponentNotices: [] };
    },
    enabled: Boolean(providerId) && options.enabled !== false,
    ...CACHED_UNTIL_INVALIDATED,
    ...options,
  });

export const useInvalidateProviderAdmissions = (providerId) => {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({
      queryKey: providerAdmissionKeys.root(providerId),
      refetchType: "all",
    });
};
